import React from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { UserProfile, MarkersOutsideOptimalDisplay } from '../../models/UserProfile';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import { LogoUpload } from '../logo/logo-upload';
import { Section } from '../../models';
import {
  bwhLabsVisible,
  supplementsVisible,
} from '../../utils/supplementsVisibility';
import { siUnitsVisible } from '../../utils/siUnitsVisibility';
import { UnitSystem } from '../../models/units/unitConversions';
import {
  DEFAULT_ANALYSIS_REPORT_TITLE,
  DEFAULT_COMPARISON_REPORT_TITLE,
  REPORT_TITLE_MAX_LENGTH,
} from '../../models/reportTitles';
import {
  INSIGHTS_PANEL_SECTIONS,
  insightsPanelPrintsInThisBuild,
} from '../bloodworkz/insights-panel-sections';
import {
  HOLOBIOME_SECTION,
  HOLOBIOME_SYSTEMS_SECTION,
  HOLOBIOME_TOP_SECTION,
  SYSTEMIC_FORMULAS_BRAND,
} from '../../models/holobiomeKits';

interface SettingsPageProps {
  userProfile: UserProfile;
  userProfileUIStore?: UserProfileUIStore;
  /** Saves the report byline. Separate because it is held locally until Save. */
  onSavePreparedBy: (preparedBy: string) => void;
  /** Saves both cover-page headings. Held locally until Save, like the byline. */
  onSaveReportTitles: (
    analysisTitle: string,
    comparisonTitle: string,
  ) => void;
  onLogoUploaded: (logoUrl: string) => void;
  onLogoRemoved: () => void;
  onError: (error: string) => void;
  onBrandsChange: (brands: string[]) => void;
  onExcludedFromPdfSectionsChange: (sections: Section[]) => void;
  /** Which sections print their bar graphs WITHOUT their Insights panel. */
  onExcludedInsightsFromPdfSectionsChange: (sections: Section[]) => void;
  onMarkersOutsideOptimalDisplayChange: (mode: MarkersOutsideOptimalDisplay) => void;
  onShowOnlySectionSignalsChange: (value: boolean) => void;
  onShowOnlyImmuneSectionSignalsChange: (value: boolean) => void;
  onShowImmuneAbsolutesChange: (value: boolean) => void;
  onShowPreviousTestMarkersInComparisonChange: (value: boolean) => void;
  /** Whether the comparison Notes column appears on screen at all. */
  onShowComparisonNotesOnScreenChange: (value: boolean) => void;
  /** Whether the comparison Notes column prints on the with-notes PDF. */
  onShowComparisonNotesInPdfChange: (value: boolean) => void;
  onShowComparisonInsightsNotesInPdfChange: (value: boolean) => void;
  onShowComparisonOverallProgressInPdfChange: (value: boolean) => void;
  /** Where the comparison table's "Hide Alarm" checkbox starts on each report. */
  onHideAlarmColumnsInComparisonChange: (value: boolean) => void;
  /** Where the comparison table's "Hide Lab" checkbox starts on each report. */
  onHideLabColumnsInComparisonChange: (value: boolean) => void;
  onUnitSystemChange: (value: UnitSystem) => void;
  onSaveSettings: () => void;
}

// The complete list of sections that appear in Test Results Analysis and can
// be toggled on/off in the PDF. Order here drives the Settings UI order.
const PDF_INCLUSION_SECTIONS: { section: Section; label: string }[] = [
  { section: Section.BMI,                           label: 'BMI' },
  { section: Section.RESULTS_OVERVIEW,              label: 'Results Overview' },
  { section: Section.MARKERS_OUTSIDE_OPTIMAL,       label: 'Markers Outside Optimal' },
  { section: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { section: Section.IRON,                          label: 'Iron Status & Utilization' },
  { section: Section.WBC,                           label: 'Immune Activity' },
  { section: Section.PLATELETS,                     label: 'Blood Clotting & Flow' },
  { section: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { section: Section.KIDNEY,                        label: 'Kidney Function' },
  { section: Section.PROTEINS,                      label: 'Protein Status' },
  { section: Section.NUTRIENTS,                     label: 'Nutrient Status' },
  { section: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { section: Section.DIGESTION,                     label: 'Digestive & Gut Health' },
  { section: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { section: Section.LIPIDS,                        label: 'Lipid Balance' },
  { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { section: Section.THYROID,                       label: 'Thyroid Function' },
  { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { section: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { section: Section.PROSTATE,                      label: 'Prostate Function' },
  { section: Section.CUSTOM_MARKERS,                label: Section.CUSTOM_MARKERS },
  // The supplement sections, in the order the report prints them: the three
  // Systemic Formulas views (each its own document, hence its own checkbox),
  // then the practitioner's curated client list.
  //
  // Section.SUPPLEMENTS is deliberately NOT here. Its panel carries every brand
  // EXCEPT Systemic Formulas, and with BWH Labs held back there is no such
  // brand to check — the panel renders nothing on every account, so its row was
  // a setting with no section behind it. Add the row back with the second
  // brand, not before.
  { section: HOLOBIOME_TOP_SECTION,                 label: 'Systemic Formulas - Top Priority' },
  { section: HOLOBIOME_SYSTEMS_SECTION,             label: 'Systemic Formulas - By System' },
  { section: HOLOBIOME_SECTION,                     label: 'Systemic Formulas - Holobiome Roadmap' },
  { section: Section.RECOMMENDED_SUPPLEMENTS,       label: 'Recommended Supplements' },
];

/**
 * The three Systemic Formulas views, which live and die with that brand's
 * checkbox in Supplement Settings further down this page.
 *
 * Pseudo-sections, not Section values (see holobiomeKits.ts). They persist into
 * UserProfile.excludedFromPdfSections exactly like a real section does, which
 * is why they can sit in the list above at all.
 */
const HOLOBIOME_PDF_SECTIONS: Section[] = [
  HOLOBIOME_TOP_SECTION,
  HOLOBIOME_SYSTEMS_SECTION,
  HOLOBIOME_SECTION,
];

/** Every row above that the supplements feature flag owns. */
const SUPPLEMENT_PDF_SECTIONS: Section[] = [
  ...HOLOBIOME_PDF_SECTIONS,
  Section.RECOMMENDED_SUPPLEMENTS,
];

/**
 * Sections whose Insights Panel can be left out of the PDF on its own, with the
 * section's bar graphs still printing.
 *
 * DERIVED, not a third hand-kept list: the sections come from
 * INSIGHTS_PANEL_SECTIONS (the one place that knows which sections have a panel)
 * and the labels from PDF_INCLUSION_SECTIONS above, so the two blocks always
 * name a section the same way and neither can list one the other doesn't.
 *
 * Filtered to the panels this build actually prints — a checkbox for one held
 * back for clinical review would be a setting with no effect. See
 * insightsPanelPrintsInThisBuild.
 */
const PDF_INSIGHTS_SECTIONS: { section: Section; label: string }[] =
  INSIGHTS_PANEL_SECTIONS.filter(insightsPanelPrintsInThisBuild).map(section => ({
    section,
    label:
      PDF_INCLUSION_SECTIONS.find(entry => entry.section === section)?.label ||
      section,
  }));

// Matches the "Prepared by: NAME" line the reports render, which is sized for
// the cover page and footer.
const PREPARED_BY_MAX_LENGTH = 32;

export const SettingsPage: React.FC<SettingsPageProps> = ({
  userProfile,
  userProfileUIStore,
  onSavePreparedBy,
  onSaveReportTitles,
  onLogoUploaded,
  onLogoRemoved,
  onError,
  onBrandsChange,
  onExcludedFromPdfSectionsChange,
  onExcludedInsightsFromPdfSectionsChange,
  onMarkersOutsideOptimalDisplayChange,
  onShowOnlySectionSignalsChange,
  onShowOnlyImmuneSectionSignalsChange,
  onShowImmuneAbsolutesChange,
  onShowPreviousTestMarkersInComparisonChange,
  onShowComparisonNotesOnScreenChange,
  onShowComparisonNotesInPdfChange,
  onShowComparisonInsightsNotesInPdfChange,
  onShowComparisonOverallProgressInPdfChange,
  onHideAlarmColumnsInComparisonChange,
  onHideLabColumnsInComparisonChange,
  onUnitSystemChange,
  onSaveSettings,
}) => {
  // Prepared By is held LOCALLY until its own Save, unlike every other field on
  // this page, which pushes to the shared profile on change. The logo upload
  // below it saves the profile the moment a file lands — with the usual
  // push-on-change it would persist a byline the practitioner was still typing
  // and had not committed to.
  const [preparedBy, setPreparedBy] = React.useState(
    userProfile.preparedBy || '',
  );

  // Held locally until their own Save for the same reason as the byline above:
  // the logo sitting between them writes the whole profile the moment a file
  // lands, and a half-typed title must not ride along with it.
  //
  // Empty means "print the default", so the stored value is what goes in the
  // box and the default is only ever the placeholder. Seeding the box with the
  // default text instead would make every practitioner's profile store a
  // hardcoded copy of it the first time they saved anything here, and a later
  // change to the shipped wording would never reach them.
  const [analysisTitle, setAnalysisTitle] = React.useState(
    userProfile.analysisReportTitle || '',
  );

  const [comparisonTitle, setComparisonTitle] = React.useState(
    userProfile.comparisonReportTitle || '',
  );

  const [enabledBrands, setEnabledBrands] = React.useState<string[]>(
    userProfile.enabledSupplementBrands || [],
  );

  const [excludedFromPdfSections, setExcludedFromPdfSections] = React.useState<Section[]>(
    userProfile.excludedFromPdfSections || [],
  );

  const [
    excludedInsightsFromPdfSections,
    setExcludedInsightsFromPdfSections,
  ] = React.useState<Section[]>(
    userProfile.excludedInsightsFromPdfSections || [],
  );

  const [markersOutsideOptimalDisplay, setMarkersOutsideOptimalDisplay] =
    React.useState<MarkersOutsideOptimalDisplay>(
      userProfile.markersOutsideOptimalDisplay || 'showBars',
    );

  const handleMarkersOutsideOptimalDisplayChange = (mode: MarkersOutsideOptimalDisplay) => {
    setMarkersOutsideOptimalDisplay(mode);
    onMarkersOutsideOptimalDisplayChange(mode);
  };

  const [showOnlySectionSignals, setShowOnlySectionSignals] = React.useState<boolean>(
    userProfile.showOnlySectionSignals ?? true,
  );

  const handleShowOnlySectionSignalsChange = (value: boolean) => {
    setShowOnlySectionSignals(value);
    onShowOnlySectionSignalsChange(value);
  };

  const [showOnlyImmuneSectionSignals, setShowOnlyImmuneSectionSignals] = React.useState<boolean>(
    userProfile.showOnlyImmuneSectionSignals ?? true,
  );

  const handleShowOnlyImmuneSectionSignalsChange = (value: boolean) => {
    setShowOnlyImmuneSectionSignals(value);
    onShowOnlyImmuneSectionSignalsChange(value);
  };

  const [showImmuneAbsolutes, setShowImmuneAbsolutes] = React.useState<boolean>(
    userProfile.showImmuneAbsolutes ?? true,
  );

  const handleShowImmuneAbsolutesChange = (value: boolean) => {
    setShowImmuneAbsolutes(value);
    onShowImmuneAbsolutesChange(value);
  };

  const [
    showPreviousTestMarkersInComparison,
    setShowPreviousTestMarkersInComparison,
  ] = React.useState<boolean>(
    userProfile.showPreviousTestMarkersInComparison ?? true,
  );

  const handleShowPreviousTestMarkersInComparisonChange = (value: boolean) => {
    setShowPreviousTestMarkersInComparison(value);
    onShowPreviousTestMarkersInComparisonChange(value);
  };

  const [showComparisonNotesOnScreen, setShowComparisonNotesOnScreen] =
    React.useState<boolean>(userProfile.showComparisonNotesOnScreen ?? true);

  const handleShowComparisonNotesOnScreenChange = (value: boolean) => {
    setShowComparisonNotesOnScreen(value);
    onShowComparisonNotesOnScreenChange(value);
  };

  const [showComparisonNotesInPdf, setShowComparisonNotesInPdf] =
    React.useState<boolean>(userProfile.showComparisonNotesInPdf ?? true);

  const handleShowComparisonNotesInPdfChange = (value: boolean) => {
    setShowComparisonNotesInPdf(value);
    onShowComparisonNotesInPdfChange(value);
  };

  // The two note blocks that print after the tables. Both default true for a
  // profile saved before they existed, which is the report as it has always
  // printed: "with notes" carried all three blocks.
  const [
    showComparisonInsightsNotesInPdf,
    setShowComparisonInsightsNotesInPdf,
  ] = React.useState<boolean>(
    userProfile.showComparisonInsightsNotesInPdf ?? true,
  );

  const handleShowComparisonInsightsNotesInPdfChange = (value: boolean) => {
    setShowComparisonInsightsNotesInPdf(value);
    onShowComparisonInsightsNotesInPdfChange(value);
  };

  const [
    showComparisonOverallProgressInPdf,
    setShowComparisonOverallProgressInPdf,
  ] = React.useState<boolean>(
    userProfile.showComparisonOverallProgressInPdf ?? true,
  );

  const handleShowComparisonOverallProgressInPdfChange = (value: boolean) => {
    setShowComparisonOverallProgressInPdf(value);
    onShowComparisonOverallProgressInPdfChange(value);
  };

  // Both default FALSE for a profile saved before they existed, which is the
  // every-column table the comparison has always opened with.
  const [hideAlarmColumnsInComparison, setHideAlarmColumnsInComparison] =
    React.useState<boolean>(userProfile.hideAlarmColumnsInComparison ?? false);

  const handleHideAlarmColumnsInComparisonChange = (value: boolean) => {
    setHideAlarmColumnsInComparison(value);
    onHideAlarmColumnsInComparisonChange(value);
  };

  const [hideLabColumnsInComparison, setHideLabColumnsInComparison] =
    React.useState<boolean>(userProfile.hideLabColumnsInComparison ?? false);

  const handleHideLabColumnsInComparisonChange = (value: boolean) => {
    setHideLabColumnsInComparison(value);
    onHideLabColumnsInComparisonChange(value);
  };

  const [unitSystem, setUnitSystem] = React.useState<UnitSystem>(
    userProfile.unitSystem === 'SI' ? 'SI' : 'US',
  );

  const handleUnitSystemChange = (value: UnitSystem) => {
    setUnitSystem(value);
    onUnitSystemChange(value);
  };

  const isIncluded = (section: Section) => !excludedFromPdfSections.includes(section);

  const handleIncludeInPdfToggle = (section: Section) => {
    const next = isIncluded(section)
      ? [...excludedFromPdfSections, section]
      : excludedFromPdfSections.filter(s => s !== section);
    setExcludedFromPdfSections(next);
    onExcludedFromPdfSectionsChange(next);
  };

  const isInsightsIncluded = (section: Section) =>
    !excludedInsightsFromPdfSections.includes(section);

  const handleIncludeInsightsInPdfToggle = (section: Section) => {
    const next = isInsightsIncluded(section)
      ? [...excludedInsightsFromPdfSections, section]
      : excludedInsightsFromPdfSections.filter(s => s !== section);
    setExcludedInsightsFromPdfSections(next);
    onExcludedInsightsFromPdfSectionsChange(next);
  };

  const handleBrandToggle = (brand: string) => {
    let newBrands = [...enabledBrands];
    if (newBrands.includes(brand)) {
      newBrands = newBrands.filter(b => b !== brand);
    } else {
      newBrands.push(brand);
    }
    setEnabledBrands(newBrands);
    onBrandsChange(newBrands);
  };

  return (
    <div>
      {/* First on the page: it is what appears at the TOP of every report, and
          it is the block a practitioner sets once when they start and then
          comes back to when their practice details change. Everything below it
          is per-section display behavior. */}
      <h5>Report Branding</h5>
      <p className="text-muted">
        Your name and logo as they appear on the reports you produce.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formPreparedBy">
          <Form.Label column sm={3}>
            Prepared By
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. MaryAnn Marks"
              value={preparedBy}
              onChange={e => {
                const value = e.target.value;
                if (value.length > PREPARED_BY_MAX_LENGTH) {
                  return;
                }
                setPreparedBy(value);
              }}
            />
            <Form.Text className="text-muted">
              When you enter a name in this field, it will appear as "Prepared
              by: NAME" on the cover page and footer of both PDF reports.
              <br />
              If you choose not to include a "Prepared by: NAME" on the reports,
              leave this field blank.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button
              variant="primary"
              onClick={() => onSavePreparedBy(preparedBy.trim())}
            >
              Save Prepared By
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Custom Logo</h5>
      <p className="text-muted">
        Manage the logo that appears on your reports.
      </p>
      <hr />

      <div className="mb-5">
        <LogoUpload
          userProfile={userProfile}
          userProfileUIStore={userProfileUIStore}
          onLogoUploaded={onLogoUploaded}
          onLogoRemoved={onLogoRemoved}
          onError={onError}
        />
      </div>

      {/* Sits with the logo and the byline because all three are the cover
          page. Below the logo rather than above it so the block a practitioner
          came here for (their logo) stays where it has always been. */}
      <h5 className="mt-4">Report Titles</h5>
      <p className="text-muted">
        The heading on the cover page of each PDF. Your own markers and sections
        can carry an organic acids, stool or hair test into the same report, so
        the titles do not name a specimen. Change them to whatever you call
        these reports in your practice, or leave a field blank to print the
        standard title.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formAnalysisReportTitle">
          <Form.Label column sm={3}>
            Analysis PDF
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '320px' }}
              placeholder={DEFAULT_ANALYSIS_REPORT_TITLE}
              value={analysisTitle}
              onChange={e => {
                const value = e.target.value;
                if (value.length > REPORT_TITLE_MAX_LENGTH) {
                  return;
                }
                setAnalysisTitle(value);
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formComparisonReportTitle">
          <Form.Label column sm={3}>
            Comparison PDF
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '320px' }}
              placeholder={DEFAULT_COMPARISON_REPORT_TITLE}
              value={comparisonTitle}
              onChange={e => {
                const value = e.target.value;
                if (value.length > REPORT_TITLE_MAX_LENGTH) {
                  return;
                }
                setComparisonTitle(value);
              }}
            />
            <Form.Text className="text-muted">
              Reports you have already exported keep the title they were
              exported with. This changes the next PDF you produce.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button
              variant="primary"
              onClick={() =>
                onSaveReportTitles(analysisTitle.trim(), comparisonTitle.trim())
              }
            >
              Save Report Titles
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">PDF Inclusion</h5>
      <p className="text-muted">
        Sets the default state of the &quot;Include in PDF&quot; checkbox in each
        section&apos;s header on the Test Results Analysis page. Unchecking a
        section here means it stays out of the PDF on every test by default. You
        can still flip the checkbox per-test to include or exclude that section
        for a specific export. These settings do not affect on-screen viewing.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formPdfInclusion">
          <Form.Label column sm={3}>
            Default sections to include
          </Form.Label>
          <Col sm={9}>
            {/* Two dozen checkboxes in one column ran most of a screen tall
                with the page's whole right half empty beside it. CSS columns
                rather than a grid of rows: the list is in report order, and
                multi-column flows top-to-bottom down one column before starting
                the next, so that order still reads straight down. A grid laid
                out in rows would scatter it left-to-right.

                columnWidth WITH columnCount makes the count a maximum: three
                columns where there is room for three, dropping to two and then
                one as the window narrows, with no media query to keep in sync.
                breakInside keeps a checkbox from splitting its box away from
                its label across a column boundary. */}
            <div
              style={{
                columnWidth: '260px',
                columnCount: 3,
                columnGap: '32px',
              }}
            >
              {PDF_INCLUSION_SECTIONS
                .filter(({ section }) => {
                  // Every supplement section goes away with the feature flag,
                  // so none of their rows may outlive it.
                  if (
                    SUPPLEMENT_PDF_SECTIONS.includes(section) &&
                    !supplementsVisible()
                  ) {
                    return false;
                  }
                  // The three Systemic Formulas views are the Holobiome card,
                  // which the brand checkbox below takes away whole. Reads the
                  // LIVE `enabledBrands` rather than the saved profile, so the
                  // rows appear and disappear as that box is ticked, with no
                  // Save in between.
                  if (HOLOBIOME_PDF_SECTIONS.includes(section)) {
                    return enabledBrands.includes(SYSTEMIC_FORMULAS_BRAND);
                  }
                  return true;
                })
                .map(({ section, label }) => (
                  <Form.Check
                    key={section}
                    type="checkbox"
                    id={`pdf-include-${section}`}
                    label={label}
                    checked={isIncluded(section)}
                    onChange={() => handleIncludeInPdfToggle(section)}
                    style={{ breakInside: 'avoid' }}
                  />
                ))}
            </div>
          </Col>
        </Form.Group>

        {/* A section's Insights panel is a second, narrower choice: the section
            prints, and separately the interpretation above its bars either
            reaches the client or stays with the practitioner. Unchecking a
            section above makes its row here moot, which is why this block sits
            under that one rather than beside it. */}
        <Form.Group as={Row} controlId="formPdfInsightsInclusion">
          <Form.Label column sm={3}>
            Insights Panels to include
          </Form.Label>
          <Col sm={9}>
            <div
              style={{
                columnWidth: '260px',
                columnCount: 3,
                columnGap: '32px',
              }}
            >
              {PDF_INSIGHTS_SECTIONS.map(({ section, label }) => (
                <Form.Check
                  key={section}
                  type="checkbox"
                  id={`pdf-include-insights-${section}`}
                  label={label}
                  checked={isInsightsIncluded(section)}
                  onChange={() => handleIncludeInsightsInPdfToggle(section)}
                  style={{ breakInside: 'avoid' }}
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              Uncheck a section to print its bar graphs without the Insight
              Panel above them. The panel stays on screen either way, and you
              can flip the section&apos;s &quot;Include Insights Panel in
              PDF&quot; checkbox per-test.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save PDF Inclusion Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      {siUnitsVisible() && (
        <>
          <h5 className="mt-4">Units</h5>
          <p className="text-muted">
            The default unit system for marker values, used when entering test
            results and throughout the report.
          </p>
          <hr />

          <Form className="mb-5">
            <Form.Group as={Row} controlId="formUnitSystem">
              <Form.Label column sm={3}>
                Default units
              </Form.Label>
              <Col sm={9}>
                <Form.Check
                  type="radio"
                  id="unit-system-us"
                  name="unitSystem"
                  label="US conventional — mg/dL, ng/mL (default)"
                  checked={unitSystem === 'US'}
                  onChange={() => handleUnitSystemChange('US')}
                />
                <Form.Check
                  type="radio"
                  id="unit-system-si"
                  name="unitSystem"
                  label="SI international — mmol/L, μmol/L, g/L"
                  checked={unitSystem === 'SI'}
                  onChange={() => handleUnitSystemChange('SI')}
                />
                <Form.Text className="text-muted">
                  SI is how labs report in Canada, Australia and most of the
                  world outside the US. This is a default: the Units setting on
                  an individual client overrides it, so a practice with clients
                  in both systems is covered.
                  <br />
                  <br />
                  Changing this never changes stored results — values and
                  reference ranges are converted for display only, so the same
                  test can be read in either system. A few markers differ by
                  more than their unit: BUN is reported as Urea in SI, and
                  ratios such as Triglycerides/HDL stay on their US-derived
                  scale so their optimal ranges keep their meaning.
                </Form.Text>
              </Col>
            </Form.Group>
          </Form>
        </>
      )}

      <h5 className="mt-4">Markers Outside Optimal</h5>
      <p className="text-muted">
        Controls how the &quot;Markers Outside Optimal&quot; section appears on the
        Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formMarkersOutsideOptimal">
          <Form.Label column sm={3}>
            Display mode
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="radio"
              id="markers-outside-optimal-show-bars"
              name="markersOutsideOptimalDisplay"
              label="Show bar graphs (default)"
              checked={markersOutsideOptimalDisplay === 'showBars'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('showBars')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-hide-bars"
              name="markersOutsideOptimalDisplay"
              label="Hide bar graphs"
              checked={markersOutsideOptimalDisplay === 'hideBars'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('hideBars')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-hide-section"
              name="markersOutsideOptimalDisplay"
              label="Hide section"
              checked={markersOutsideOptimalDisplay === 'hideSection'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('hideSection')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-minimize-section"
              name="markersOutsideOptimalDisplay"
              label="Minimize section"
              checked={markersOutsideOptimalDisplay === 'minimizeSection'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('minimizeSection')}
            />
            <Form.Text className="text-muted">
              &quot;Show bar graphs&quot; opens the section with reference-range
              bars visible in each panel. &quot;Hide bar graphs&quot; opens the
              section with each panel collapsed — only the marker name, value, and
              HIGH/LOW badge are shown until you click the panel to expand it.
              &quot;Hide section&quot; removes the entire section from the page.
              &quot;Minimize section&quot; collapses the whole section to its
              header — click the header to open it. Only &quot;Hide section&quot;
              affects the PDF; a minimized section still exports in full.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Markers Outside Optimal Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Inflammation Section</h5>
      <p className="text-muted">
        Controls which markers appear in the Inflammation &amp; Oxidative
        Stress section on the Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowOnlySectionSignals">
          <Form.Label column sm={3}>
            Marker visibility
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-only-section-signals"
              label="Only show markers with a section signal"
              checked={showOnlySectionSignals}
              onChange={() => handleShowOnlySectionSignalsChange(!showOnlySectionSignals)}
            />
            <Form.Text className="text-muted">
              When checked (default), cross-section markers (e.g. ferritin,
              GGT, albumin, HDL) only appear in the Inflammation section
              when their value contributes a signal there — markers that
              are optimal or out of optimal in the opposite direction
              (e.g. high albumin, low ferritin) are hidden, since they have
              a home in another section. Markers whose only home is this
              section (hs-CRP, ESR, homocysteine) always remain visible.
              If filtering empties the section, it disappears entirely.
              Uncheck to show every marker assigned to the section.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Inflammation Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Immune Section</h5>
      <p className="text-muted">
        Controls which markers appear in the Immune Activity section on the
        Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowOnlyImmuneSectionSignals">
          <Form.Label column sm={3}>
            Marker visibility
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-only-immune-section-signals"
              label="Only show markers with a section signal"
              checked={showOnlyImmuneSectionSignals}
              onChange={() => handleShowOnlyImmuneSectionSignalsChange(!showOnlyImmuneSectionSignals)}
            />
            <Form.Text className="text-muted">
              When checked (default), cross-section markers only appear in
              the Immune section when their value contributes a signal
              there. For example, alkaline phosphatase is hidden when
              optimal or high — only a LOW Alk Phos signals low zinc,
              which the immune system needs. Globulin is hidden when
              optimal. Markers whose only home is this section (WBC and
              the differentials) always remain visible. If filtering
              empties the section, it disappears entirely. Uncheck to show
              every marker assigned to the section.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formShowImmuneAbsolutes">
          <Form.Label column sm={3}>
            Absolute differentials
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-immune-absolutes"
              label="Show absolutes"
              checked={showImmuneAbsolutes}
              onChange={() => handleShowImmuneAbsolutesChange(!showImmuneAbsolutes)}
            />
            <Form.Text className="text-muted">
              When checked (default), the absolute differential bar graphs
              (Absolute Neutrophils, Lymphocytes, Monocytes, Eosinophils,
              Basophils) are shown in the Immune Activity section. Uncheck to
              hide those bar graphs. The percentage differentials always
              remain visible.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Immune Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      {/* My Markers moved to its own page (/my-markers, under the practitioner
          menu) — it is a data-management screen rather than a display
          preference, and it was getting lost among these toggles. */}

      <h5 className="mt-4">Test Comparison Bar Graphs</h5>
      <p className="text-muted">
        Controls which markers get a reference-range bar graph on the Test
        Results Analysis page and PDF when a report compares the current test
        to a previous one.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowPreviousTestMarkers">
          <Form.Label column sm={3}>
            Previous-test markers
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-previous-test-markers"
              label="Show markers only in the previous test"
              checked={showPreviousTestMarkersInComparison}
              onChange={() =>
                handleShowPreviousTestMarkersInComparisonChange(
                  !showPreviousTestMarkersInComparison,
                )
              }
            />
            <Form.Text className="text-muted">
              When checked (default), a comparison report also shows a bar graph
              for each marker that has a result in the previous test but not the
              current one, plotting just the previous (blue) blood drop and
              tagging the row &quot;prev only&quot;. This gives a complete
              side-by-side even when the current test dropped a marker — for
              example, when a section improved to a thumbs-up because markers
              that were out of range last time are no longer being measured.
              Uncheck to show only markers present in the current test. This has
              no effect when viewing a single test.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Test Comparison Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Comparison Table Columns</h5>
      <p className="text-muted">
        Which range columns the Test Results Comparison table opens with. The
        Hide Alarm and Hide Lab checkboxes above the table still change it for
        the report in front of you; this is where they start.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formComparisonRangeColumns">
          <Form.Label column sm={3}>
            Range columns
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="hide-alarm-columns-in-comparison"
              label="Hide Alarm Min and Alarm Max"
              checked={hideAlarmColumnsInComparison}
              onChange={() =>
                handleHideAlarmColumnsInComparisonChange(
                  !hideAlarmColumnsInComparison,
                )
              }
            />
            <Form.Check
              type="checkbox"
              id="hide-lab-columns-in-comparison"
              label="Hide Lab Min and Lab Max"
              checked={hideLabColumnsInComparison}
              onChange={() =>
                handleHideLabColumnsInComparisonChange(
                  !hideLabColumnsInComparison,
                )
              }
            />
            <Form.Text className="text-muted">
              Both are unchecked by default, so the comparison table opens with
              every range column showing. Check one to have that pair of columns
              start hidden on every comparison you open, which is the narrower
              table a practitioner who works from the optimal range wants. The
              Optimal Min and Optimal Max columns always show. The PDF export
              prints whatever the table on screen shows, so a column you bring
              back with the header checkbox prints again.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Comparison Table Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Comparison Table Notes</h5>
      <p className="text-muted">
        Controls the Notes column on the right of each table in the Test
        Results Comparison report, and the two note blocks that print after
        the tables.
      </p>
      <hr />

      <Form className="mb-5">
        {/* Where the column appears, then what LabSmarts puts in it. The two
            places are separate settings because they are used separately: a
            practitioner who works on paper takes the column off the screen
            and keeps it in the PDF, where the empty boxes are writing room. */}
        <Form.Group as={Row} controlId="formShowComparisonNotesColumn">
          <Form.Label column sm={3}>
            Notes column
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-comparison-notes-on-screen"
              label="Show on the Comparison Table page"
              checked={showComparisonNotesOnScreen}
              onChange={() =>
                handleShowComparisonNotesOnScreenChange(
                  !showComparisonNotesOnScreen,
                )
              }
            />
            <Form.Check
              type="checkbox"
              id="show-comparison-notes-in-pdf"
              label="Print on the Comparison Table PDF"
              checked={showComparisonNotesInPdf}
              onChange={() =>
                handleShowComparisonNotesInPdfChange(!showComparisonNotesInPdf)
              }
            />
            <Form.Text className="text-muted">
              Both are checked by default. On screen, the column is where
              notes are typed, one per section. In the PDF it prints beside
              every section on &quot;Export to PDF w/ notes&quot;, whether
              or not a note was typed — the empty ones leave room to write on
              a printed copy. &quot;Export to PDF w/o notes&quot; never
              prints it. Unchecking either one hides the column there and
              nothing else: notes already saved are kept, still print wherever
              the column is shown, and come back if it is switched on again.
            </Form.Text>
          </Col>
        </Form.Group>

        {/* The blocks that print AFTER the tables. Separate from the column
            above because they are separate pieces of the report: a
            practitioner can want the per-section Notes beside each table and
            not the summaries that follow them. Both start checked, which is
            what "with notes" has always printed. */}
        <Form.Group as={Row} controlId="formComparisonPdfNoteBlocks">
          <Form.Label column sm={3}>
            Notes after the tables
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-comparison-insights-notes-in-pdf"
              label="Print &quot;Insights &amp; Section Notes by Collection Date&quot;"
              checked={showComparisonInsightsNotesInPdf}
              onChange={() =>
                handleShowComparisonInsightsNotesInPdfChange(
                  !showComparisonInsightsNotesInPdf,
                )
              }
            />
            <Form.Check
              type="checkbox"
              id="show-comparison-overall-progress-in-pdf"
              label="Print &quot;Overall Progress &amp; Trends&quot;"
              checked={showComparisonOverallProgressInPdf}
              onChange={() =>
                handleShowComparisonOverallProgressInPdfChange(
                  !showComparisonOverallProgressInPdf,
                )
              }
            />
            <Form.Text className="text-muted">
              Both are checked by default, and both print only on
              &quot;Export to PDF w/ notes&quot;. Uncheck either one to leave
              that block out of the PDF — the Notes column beside each table
              is unaffected, and nothing typed is deleted. Neither block
              appears on screen; they are built for the report. These set
              where each client STARTS: the &quot;Include in Export to PDF
              w/ Notes&quot; checkboxes under the Comparison Table header
              change any of the three for the client on screen, and go back to
              these settings when you open another client.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Comparison Notes Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      {supplementsVisible() && (
        <>
          <h5>Supplement Settings</h5>
          <hr />

          <Form className="mb-5">
            <Form.Group as={Row} controlId="formBrands">
              <Form.Label column sm={3}>
                Enabled Brands
              </Form.Label>
              <Col sm={9}>
                <Form.Check
                  type="checkbox"
                  id="brand-systemic-formulas"
                  label="Systemic Formulas (Use code LABSMARTS10 to get 10% off your first order.)"
                  checked={enabledBrands.includes('systemicFormulas')}
                  onChange={() => handleBrandToggle('systemicFormulas')}
                />
                {/* Pre-launch: hidden everywhere, local dev included, until
                    the BWH Labs partnership and its affiliate code are
                    finalized (see bwhLabsVisible()). */}
                {bwhLabsVisible() && (
                  <Form.Check
                    type="checkbox"
                    id="brand-bwh-labs"
                    label="BWH Labs (Use code LABSMARTS10 to get 10% off your first order.)"
                    checked={enabledBrands.includes('bwhLabs')}
                    onChange={() => handleBrandToggle('bwhLabs')}
                  />
                )}
                <Form.Text className="text-muted">
                  Select which supplement brands to display in the Recommendations
                  tab. Results are updated automatically when you save.
                </Form.Text>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Col sm={{ span: 9, offset: 3 }}>
                <Button variant="primary" onClick={onSaveSettings}>
                  Save Supplement Settings
                </Button>
              </Col>
            </Form.Group>
          </Form>
        </>
      )}
    </div>
  );
};
